import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe, Menu, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import logo from "@/assets/ansen-logo.png";

const NAV = [
  { key: "home", to: "/" },
  { key: "about", to: "/about" },
  { key: "services", to: "/services" },
  { key: "products", to: "/products" },
  { key: "quality", to: "/quality" },
  { key: "insights", to: "/insights" },
  { key: "contact", to: "/contact" },
];

const LANGS = [
  { code: "en", label: "English" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "ar", label: "العربية" },
  { code: "zh", label: "中文" },
];

export const Header = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const linkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors hover:text-primary ${
      isActive ? "text-primary" : "text-foreground/80"
    }`;

  return (
    <header className="sticky top-0 z-40 border-b bg-white/80 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" data-testid="header-logo-link" className="flex items-center shrink-0">
            <img src={logo} alt="ANSEN Indonesia" className="h-8 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {NAV.map((item) => (
              <NavLink key={item.key} to={item.to} end={item.to === "/"} className={linkCls} data-testid={`nav-link-${item.key}`}>
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 px-2" data-testid="lang-switcher-trigger">
                  <Globe className="h-4 w-4" />
                  <span className="text-xs font-bold uppercase">{i18n.resolvedLanguage}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none">
                {LANGS.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    data-testid={`lang-switcher-${l.code}`}
                    onClick={() => i18n.changeLanguage(l.code)}
                    className="flex items-center justify-between gap-4 cursor-pointer"
                  >
                    {l.label}
                    {i18n.resolvedLanguage === l.code && <Check className="h-4 w-4 text-primary" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button asChild size="sm" className="hidden md:inline-flex rounded-none font-semibold" data-testid="header-cta-quote">
              <Link to="/contact">{t("nav.requestQuote")}</Link>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden px-2"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t bg-white" data-testid="mobile-nav">
          <nav className="mx-auto max-w-7xl px-4 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${item.key}`}
                className={({ isActive }) =>
                  `px-2 py-2.5 text-sm font-medium border-s-2 ${
                    isActive ? "border-primary text-primary" : "border-transparent text-foreground/80"
                  }`
                }
              >
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
            <Button asChild className="mt-3 rounded-none font-semibold" data-testid="mobile-cta-quote">
              <Link to="/contact" onClick={() => setOpen(false)}>{t("nav.requestQuote")}</Link>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};
